// src/contexts/AcademicYearContext.js
import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from "react";
import { supabase } from "../config/supabase";
import { useAuth } from "./AuthContext";

// Sane fallback in case the settings row is missing (e.g. fresh local DB).
const DEFAULT_ACADEMIC_YEAR = "2025-2026";

const AcademicYearContext = createContext({
	currentAcademicYear: DEFAULT_ACADEMIC_YEAR,
	loading: true,
	refreshAcademicYear: async () => {},
	setAcademicYearOptimistic: () => {},
});

export const AcademicYearProvider = ({ children }) => {
	const { user, loading: authLoading } = useAuth();
	const [currentAcademicYear, setCurrentAcademicYear] = useState(
		DEFAULT_ACADEMIC_YEAR
	);
	const [loading, setLoading] = useState(true);

	const refreshAcademicYear = useCallback(async ({ silent = false } = {}) => {
		if (!silent) setLoading(true);

		try {
			const {
				data: { session },
			} = await supabase.auth.getSession();

			// app_settings is readable only for authenticated users (RLS).
			if (!session) {
				setCurrentAcademicYear(DEFAULT_ACADEMIC_YEAR);
				return;
			}

			const { data, error } = await supabase
				.from("app_settings")
				.select("value")
				.eq("key", "current_academic_year")
				.maybeSingle();

			if (error) throw error;

			if (data?.value) {
				setCurrentAcademicYear(data.value);
			} else {
				console.warn("current_academic_year not found in app_settings");
				setCurrentAcademicYear(DEFAULT_ACADEMIC_YEAR);
			}
		} catch (err) {
			console.error("Error loading current_academic_year:", err);
		} finally {
			if (!silent) setLoading(false);
		}
	}, []);

	// Optimistic update so UI reflects the new year immediately after a
	// settings save, without waiting for a round-trip.
	const setAcademicYearOptimistic = useCallback((year) => {
		if (year) setCurrentAcademicYear(year);
	}, []);

	// Wait for auth to finish, then always load the year from the database.
	useEffect(() => {
		if (authLoading) {
			setLoading(true);
			return;
		}
		refreshAcademicYear();
	}, [authLoading, user?.id, refreshAcademicYear]);

	// Re-check the database when the user returns to the tab (silent refresh).
	useEffect(() => {
		if (!user?.id) return;

		const handleVisibility = () => {
			if (document.visibilityState === "visible") {
				refreshAcademicYear({ silent: true });
			}
		};

		window.addEventListener("focus", handleVisibility);
		document.addEventListener("visibilitychange", handleVisibility);

		return () => {
			window.removeEventListener("focus", handleVisibility);
			document.removeEventListener("visibilitychange", handleVisibility);
		};
	}, [user?.id, refreshAcademicYear]);

	const value = useMemo(
		() => ({
			currentAcademicYear,
			loading,
			refreshAcademicYear,
			setAcademicYearOptimistic,
		}),
		[currentAcademicYear, loading, refreshAcademicYear, setAcademicYearOptimistic]
	);

	return (
		<AcademicYearContext.Provider value={value}>
			{children}
		</AcademicYearContext.Provider>
	);
};

export const useAcademicYear = () => {
	const ctx = useContext(AcademicYearContext);
	if (!ctx) {
		throw new Error(
			"useAcademicYear must be used within an AcademicYearProvider"
		);
	}
	return ctx;
};

// Build a small list of selectable years given the current one. Always
// includes the current year, the immediately previous one, and the next.
// Format expected: "YYYY-YYYY" (e.g. "2025-2026").
export const buildYearOptions = (currentYear) => {
	const fallback = [DEFAULT_ACADEMIC_YEAR];
	if (!currentYear || !/^\d{4}-\d{4}$/.test(currentYear)) {
		return fallback;
	}
	const [startStr, endStr] = currentYear.split("-");
	const start = parseInt(startStr, 10);
	const end = parseInt(endStr, 10);
	if (Number.isNaN(start) || Number.isNaN(end)) return fallback;

	const years = new Set();
	for (let offset = -3; offset <= 1; offset += 1) {
		years.add(`${start + offset}-${end + offset}`);
	}
	return Array.from(years).sort();
};

export default AcademicYearContext;
